import { useRouter } from "next/router";
import { useCurrentUser } from "queries/current-user";
import { isUUID } from "queries/lib/uuid";
import { useSyncExternalStore } from "react";
import { getSession } from "ui/containers/session";
import { safeLocalStorage } from "ui/lib/safe-storage";
import { useBootcamps } from "ui/lib/useBootcamps";

const KEY = "apollo-last-team";

export type LastTeam = { id: string; name: string };
type Stored = LastTeam & { userId: string; session?: string };

const listeners = new Set<() => void>();

// Read from storage once, then held here: each tab keeps its own team even
// when another tab writes a different one, and when storage is blocked.
let memory: Stored | null | undefined;

const parse = (raw: string | null): Stored | null => {
  try {
    const team = JSON.parse(raw || "null");
    return typeof team?.userId === "string" &&
      isUUID(team.id) &&
      typeof team.name === "string"
      ? {
          userId: team.userId,
          id: team.id,
          name: team.name,
          session: typeof team.session === "string" ? team.session : undefined,
        }
      : null;
  } catch {
    return null;
  }
};

const readStored = (): Stored | null => {
  if (memory === undefined) memory = parse(safeLocalStorage.getItem(KEY));
  return memory;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * A one-way fingerprint of the session cookie (cyrb53), so the stored team
 * only stands in for the session that stored it. The token is never stored.
 */
export const sessionFingerprint = (token?: string | null): string | null => {
  if (!token) return null;
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < token.length; i++) {
    const ch = token.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};

// A `?session-token=` sign-in swaps the cookie after the first render, so
// while it's in the URL the cookie still belongs to the previous session.
const sessionSwapPending = () =>
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("session-token");

const storedForThisSession = (stored: Stored) =>
  !!stored.session &&
  !sessionSwapPending() &&
  stored.session === sessionFingerprint(getSession());

/** The team the shell last showed this user, so other routes and the next visit keep it. */
export const rememberTeam = (team: LastTeam, userId?: string) => {
  if (!userId || !isUUID(team?.id) || !team.name) return;
  const session = sessionFingerprint(getSession()) ?? undefined;
  const current = readStored();
  if (
    current?.userId === userId &&
    current.id === team.id &&
    current.name === team.name &&
    current.session === session
  )
    return;
  memory = { userId, id: team.id, name: team.name, session };
  safeLocalStorage.setItem(KEY, JSON.stringify(memory));
  listeners.forEach((listener) => listener());
};

/** Sign-out forgets the team, so the next account never starts on it. */
export const forgetTeam = () => {
  memory = null;
  safeLocalStorage.removeItem(KEY);
  listeners.forEach((listener) => listener());
};

/**
 * Null on the server and during hydration, so both renders agree. While the
 * current user loads, the stored team stands in so the shell can paint it,
 * but only for the session cookie that stored it.
 */
export const useLastTeam = (): LastTeam | null => {
  const { currentUser, isLoading } = useCurrentUser();
  const stored = useSyncExternalStore(subscribe, readStored, () => null);
  if (!stored) return null;
  if (!currentUser) {
    return isLoading && storedForThisSession(stored) ? stored : null;
  }
  return stored.userId === currentUser.id ? stored : null;
};

/**
 * The team for links off a team route: the last team shown if the user still
 * has it, else their first. The stored team stands in only while the team
 * list loads.
 */
export const useFallbackTeamId = (): string | undefined => {
  const lastTeam = useLastTeam();
  const { bootcamps } = useBootcamps();
  if (!bootcamps?.length) return lastTeam?.id;
  return (
    bootcamps.find(({ id }) => id === lastTeam?.id)?.id ?? bootcamps[0]?.id
  );
};

/** The team the shell is about: the route's, or the fallback off team routes. */
export const useShellTeamId = (): string | undefined => {
  const { query } = useRouter();
  const fallback = useFallbackTeamId();
  return isUUID(query.bootcampId) ? (query.bootcampId as string) : fallback;
};

/** The shell team's calendar, or `/programs` (a server redirect) with no team yet. */
export const useHomeHref = () => {
  const teamId = useShellTeamId();
  return teamId ? `/programs/${teamId}/overview` : "/programs";
};
